import { RootState } from '@/store/store';
import { useActiveTab, useAppFilters } from '@/hooks/shared';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { ASSET_STUDIO, FILTER_PARAMS } from '@/types/shared/navigation-tabs.enums';
import { Diagnostics } from './MainContent/Diagnostics/Diagnostics';
import { AssetStudioSidePane } from './SidePane/AssetStudioSidePane';
import { setSelectedComponentObj } from '@/store/slices/assetStudioSlice';
import { useDispatch } from 'react-redux';
import { useFetchDiagnostics } from '@/hooks/AssetsStudioPage/shared/useFetchDiagnostics';
import { sortComponentsByHealthStatusAndName, componentHasCamera } from './SidePane/ComponentSelector/util';
import { MaintenanceTable } from '@/modules/shared/Maintenance/MaintenanceTable';
import { Alarms } from './MainContent/Alarms/Alarms';
import { cn } from '@/lib/utils';
import { NewSensorData } from './MainContent/SensorData/NewSensorData';
import { Minimize2, Maximize2 } from 'lucide-react';
import * as Popover from '@radix-ui/react-popover';
import { ComponentCard } from '@/components/uiKit/ComponentsFilter/ComponentCard';
import { AssetImage } from './SidePane/AssetImage/AssetImage';
import { useFetchResource } from '@/hooks/shared';
import { NewVisualAI } from './MainContent/VisualAI/NewVisualAI/NewVisualAI';

// Thumbnail component for collapsed Asset Overview card
const AssetImageThumbnail = ({ assetImageResourceId }: { assetImageResourceId: string }) => {
  const { resource: imageURL, isLoadingResource } = useFetchResource(assetImageResourceId);

  if (isLoadingResource) {
    return <div className="w-[40px] h-[40px] bg-gray-200 dark:bg-gray-700 rounded animate-pulse flex-shrink-0" />;
  }

  return imageURL ? (
    <div
      className="w-[28px] h-[28px] rounded overflow-hidden flex-shrink-0 bg-gray-100 dark:bg-gray-800"
      style={{ transform: 'rotate(180deg)' }}
    >
      <img src={imageURL} className="w-full h-full object-cover" alt="Asset thumbnail" />
    </div>
  ) : (
    <div className="w-[28px] h-[28px] bg-gray-200 dark:bg-gray-700 rounded flex items-center justify-center flex-shrink-0">
      <span className="text-[6px] text-gray-400">No Img</span>
    </div>
  );
};

interface AssetStudioProps {
  showCardsInCollapsedView?: boolean; // Controls whether to show vertical cards in collapsed view or just the button
}

export const AssetStudio = ({ showCardsInCollapsedView = false }: AssetStudioProps = {}) => {
  const dispatch = useDispatch();

  const {
    dateRange: { from, to },
    selectedDiagnostic,
  } = useSelector((state: RootState) => state.assetsStudio);
  const [maintenanceCounters, setMaintenanceCounters] = useState({ total: 0, components: {} });
  const [alarmsCounters, setAlarmCounters] = useState({ total: 0, components: {} });
  const [isLeftPanelCollapsed, setIsLeftPanelCollapsed] = useState(false);
  const { isAlarms, isMaintenance, isDiagnostics, isSensorData, isVisualAI } = useActiveTab();
  const { setFilters, selectedComponent, assetStudio } = useAppFilters();

  const memoizedFrom = useMemo(() => new Date(from), [from]);
  const memoizedTo = useMemo(() => new Date(to), [to]);

  // Trigger window resize event when panel collapses/expands to resize Plotly charts
  useEffect(() => {
    // Use setTimeout to ensure the panel animation completes before resize
    const timer = setTimeout(() => {
      window.dispatchEvent(new Event('resize'));
    }, 300); // Match this with CSS transition duration

    return () => clearTimeout(timer);
  }, [isLeftPanelCollapsed]);

  const { diagnostics, getDiagnostics, isLoading, isFetching, components, asset } = useFetchDiagnostics(
    memoizedFrom,
    memoizedTo
  );

  // Dates are not included in the useFetchDiagnostics to allow reuseablity of the hook
  useEffect(() => {
    getDiagnostics();
  }, [from, to]);

  // @TODO: check if you can avoid the 2 logics in the useEffect
  useEffect(() => {
    setFilters({ [FILTER_PARAMS.SELECTED_COMPONENT]: selectedComponent });
    //  if all component are selected there is no selected object
    if (!selectedComponent || selectedComponent === ASSET_STUDIO.ALL_COMPONENTS) {
      dispatch(setSelectedComponentObj(null));
      return;
    }
    const currComponent = components?.find((el: any) => el.component_id === selectedComponent);
    if (currComponent) {
      const hasCamera = componentHasCamera(currComponent);
      // Create a new object to avoid modifying a frozen one
      dispatch(setSelectedComponentObj({ ...currComponent, hasCamera }));

      // IMPORTANT: For tab redirects, check camera capability from LATEST diagnostics, not selected diagnostic date
      // This ensures correct behavior regardless of which date is selected on the timeline
      const latestComponents = diagnostics?.[diagnostics?.length - 1]?.[1]?.assets?.[0]?.components;
      const latestComponent = latestComponents?.find((c: any) => c.component_id === selectedComponent);
      const hasCameraCapability = latestComponent ? componentHasCamera(latestComponent) : false;

      // If currently on Visual AI tab and the new component doesn't have a camera, redirect to Diagnostics
      if (assetStudio === ASSET_STUDIO.VISUAL_AI && !hasCameraCapability) {
        setFilters({ [FILTER_PARAMS.ASSET_STUDIO]: ASSET_STUDIO.DIAGNOSTICS });
      }
      // If currently on Data Studio tab and the new component is Visual AI component ( has camera ), redirect to Diagnostics
      if (assetStudio === ASSET_STUDIO.SENSOR_DATA && hasCameraCapability) {
        setFilters({ [FILTER_PARAMS.ASSET_STUDIO]: ASSET_STUDIO.DIAGNOSTICS });
      }
    }
  }, [selectedComponent, assetStudio, components, diagnostics, setFilters, dispatch]);

  const firstComponentsList = useMemo(
    () => sortComponentsByHealthStatusAndName(diagnostics?.[diagnostics?.length - 1]?.[1]?.assets?.[0]?.components),
    [diagnostics, components]
  );

  const currentComponents = useMemo(() => {
    const selectedDiagnosticComponents = selectedDiagnostic?.[1]?.assets?.[0]?.components;
    return Array.isArray(selectedDiagnosticComponents) ? selectedDiagnosticComponents : components;
  }, [selectedDiagnostic, components]);

  const renderTabContent = useCallback(() => {
    switch (assetStudio) {
      case ASSET_STUDIO.DIAGNOSTICS:
        return (
          <Diagnostics
            components={currentComponents}
            asset={asset}
            diagnostics={diagnostics}
            isLoading={isLoading || isFetching}
            isLeftPanelCollapsed={isLeftPanelCollapsed}
          />
        );
      case ASSET_STUDIO.SENSOR_DATA:
        return <NewSensorData asset={asset} components={currentComponents} diagnostics={diagnostics} />;
      case ASSET_STUDIO.VISUAL_AI:
        return (
          <NewVisualAI
            asset={asset}
            components={components}
            diagnostics={diagnostics}
            isLoadingDiagnostics={isLoading || isFetching}
          />
        );
      case ASSET_STUDIO.MAINTENANCE_DATA:
        return (
          <MaintenanceTable
            components={currentComponents}
            enableEditing={true}
            setMaintenanceCounters={setMaintenanceCounters}
          />
        );
      case ASSET_STUDIO.ALARMS:
        return <Alarms setAlarmsCounters={setAlarmCounters} />;
      default:
        return null;
    }
  }, [assetStudio, selectedComponent, diagnostics, isLoading, isFetching, components, asset, currentComponents]);
  return (
    <>
      <div
        className={cn(
          'h-[calc(100vh-11rem)] flex relative overflow-hidden',
          showCardsInCollapsedView || !isLeftPanelCollapsed ? 'gap-4' : 'gap-0'
        )}
      >
        <div
          className={cn(
            'flex flex-col gap-4= h-full overflow-hidden sticky top-0 z-20 group',
            isLeftPanelCollapsed ? 'w-[32px] min-w-[32px]' : 'w-1/5 min-w-[250px]'
          )}
        >
          {isLeftPanelCollapsed ? (
            <div className="flex flex-col h-full bg-white dark:bg-dark-bg-primary border-r-0 border-gray-200 dark:border-gray-700 overflow-y-auto overflow-x-hidden no-scrollbar">
              {/* Expand Icon at Top */}
              <div className="sticky top-0 z-20 bg-white dark:bg-dark-bg-primary border-b border-gray-200 dark:border-gray-700 py-2 px-0.5 flex justify-center">
                <button
                  onClick={() => setIsLeftPanelCollapsed(false)}
                  className="p-1 hover:bg-gray-100 dark:hover:bg-gray-800 rounded transition-colors"
                  title="Expand panel"
                >
                  <Maximize2 className="h-4 w-4 text-gray-600 dark:text-gray-400" />
                </button>
              </div>

              {/* Component Navigation - Only shown if showCardsInCollapsedView is true */}
              {showCardsInCollapsedView && (
                <div className="flex flex-col gap-1 px-0.5 py-2">
                  {/* Asset Overview - First Card */}
                  <Popover.Root>
                    <Popover.Trigger asChild>
                      <div
                        onClick={() =>
                          !(isSensorData || isVisualAI) &&
                          setFilters({ [FILTER_PARAMS.SELECTED_COMPONENT]: ASSET_STUDIO.ALL_COMPONENTS })
                        }
                        className={cn(
                          'w-full h-[140px] px-1 py-2 rounded-md flex flex-col items-center justify-between overflow-visible transition-all cursor-pointer shadow-sm',
                          isSensorData || isVisualAI
                            ? 'bg-gray-100 dark:bg-gray-800 cursor-not-allowed opacity-50'
                            : selectedComponent === ASSET_STUDIO.ALL_COMPONENTS
                            ? 'bg-primary-100 dark:bg-primary-900/30 ring-2 ring-primary-500 shadow-lg'
                            : 'bg-primary-50 dark:bg-primary-900/20 hover:bg-primary-100 dark:hover:bg-primary-900/30 hover:shadow-md'
                        )}
                      >
                        {/* Asset Overview Text - Appears First */}
                        <span
                          className={cn(
                            'text-[10px] font-bold whitespace-nowrap',
                            isSensorData || isVisualAI
                              ? 'text-gray-400 dark:text-gray-600'
                              : 'text-primary-700 dark:text-primary-300'
                          )}
                          style={{
                            writingMode: 'vertical-lr',
                            textOrientation: 'mixed',
                            transform: 'rotate(180deg)',
                            maxHeight: '90px',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          Asset Overview
                        </span>

                        {/* Asset Image Thumbnail - Appears After Text */}
                        <div className="flex-shrink-0">
                          {asset?.asset_image_resource_id ? (
                            <AssetImageThumbnail assetImageResourceId={asset.asset_image_resource_id} />
                          ) : (
                            <div className="w-[28px] h-[28px] bg-gray-200 dark:bg-gray-700 rounded flex items-center justify-center flex-shrink-0">
                              <span className="text-[6px] text-gray-400">No Img</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </Popover.Trigger>
                    <Popover.Portal>
                      <Popover.Content
                        side="right"
                        align="start"
                        sideOffset={8}
                        className="z-[100] w-[280px] animate-in fade-in-0 zoom-in-95"
                        collisionPadding={8}
                      >
                        <div className="rounded-lg shadow-lg bg-white dark:bg-dark-bg-primary border border-gray-200 dark:border-gray-700 overflow-hidden">
                          <AssetImage asset={asset} isLoading={isLoading || isFetching} />
                        </div>
                      </Popover.Content>
                    </Popover.Portal>
                  </Popover.Root>

                  {/* Component Cards */}
                  {firstComponentsList?.map((comp: any, index: number) => {
                    const isSelected = comp.component_id === selectedComponent;
                    const healthColors: Record<string, string> = {
                      healthy: '#22c55e',
                      alarm: '#f59e0b',
                      monitor: '#eab308',
                      critical: '#ef4444',
                      na: '#9ca3af',
                    };
                    const componentName = comp.component_name || comp.name || '';
                    const healthColor = healthColors[comp.health_status] || healthColors.na;
                    return (
                      <Popover.Root key={comp.component_id || index}>
                        <Popover.Trigger asChild>
                          <div
                            onClick={() => setFilters({ [FILTER_PARAMS.SELECTED_COMPONENT]: comp.component_id })}
                            className={cn(
                              'relative w-full h-[160px] px-2 py-2 cursor-pointer transition-all hover:bg-gray-50 hover:shadow-md dark:hover:bg-dark-bg-sidebar flex items-center justify-center rounded-md shadow-sm overflow-visible',
                              isSelected && 'ring-2 ring-primary-500 shadow-lg'
                            )}
                            style={{
                              borderBottom: `4px solid ${healthColor}`,
                              backgroundColor: isSelected ? 'rgba(59, 130, 246, 0.1)' : undefined,
                            }}
                          >
                            <span
                              className="text-[10px] font-semibold text-gray-700 dark:text-gray-300 whitespace-nowrap"
                              style={{
                                writingMode: 'vertical-lr',
                                textOrientation: 'mixed',
                                transform: 'rotate(180deg)',
                                maxHeight: '140px',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                              }}
                            >
                              {componentName.slice(0, 40)}
                            </span>
                          </div>
                        </Popover.Trigger>
                        <Popover.Portal>
                          <Popover.Content
                            side="right"
                            align="start"
                            sideOffset={8}
                            className="z-[100] w-[280px] animate-in fade-in-0 zoom-in-95"
                            collisionPadding={8}
                          >
                            <div className="rounded-lg shadow-lg">
                              <ComponentCard
                                item={{
                                  ...comp,
                                  name: comp.component_name || comp.name,
                                  id: comp.component_id,
                                  health_status: comp.health_status,
                                  diagnostics: comp.diagnostics,
                                }}
                                isSelected={isSelected}
                                onClick={() => setFilters({ [FILTER_PARAMS.SELECTED_COMPONENT]: comp.component_id })}
                                showHealthStatus={true}
                              />
                            </div>
                          </Popover.Content>
                        </Popover.Portal>
                      </Popover.Root>
                    );
                  })}
                </div>
              )}
            </div>
          ) : (
            <div className="relative h-full w-full">
              {/* Collapse Icon at Top Right of Expanded Panel */}
              <div className="absolute top-2 right-2 z-10">
                <button
                  onClick={() => setIsLeftPanelCollapsed(true)}
                  className="p-1 hover:bg-gray-100 dark:hover:bg-gray-800 rounded transition-colors"
                  title="Collapse panel"
                >
                  <Minimize2 className="h-4 w-4 text-gray-600 dark:text-gray-400" />
                </button>
              </div>

              {
                //@TODO: check if mobile is affected by current_component if not, replace with components={current_component}
              }
              <AssetStudioSidePane
                asset={asset}
                isLoading={isLoading || isFetching}
                components={components}
                current_components={firstComponentsList}
                additionalCounters={isAlarms ? alarmsCounters : isMaintenance ? maintenanceCounters : null}
              />
            </div>
          )}
        </div>

        <div className={cn('flex h-full flex-col relative', isLeftPanelCollapsed ? 'flex-1' : 'w-4/5')}>
          {renderTabContent()}
        </div>
      </div>
    </>
  );
};
